import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Nissan Skyline GT-R (R34, ~1999) — the iconic JDM sports coupe at true scale
// (1 unit = 1 metre): length 4.60, width 1.79 (blistering wider over the
// arches), height ~1.36, wheelbase 2.67 (front axle +1.15, rear -1.52), on big
// ~0.36 m wheels pushed out to the body edges. Nose is +Z. Authored for the
// `cars` showcase batch.
//
// The silhouette is a LOW, WIDE, ANGULAR two-door NOTCHBACK coupe — a planted,
// muscular three-box body, NOT a fastback and NOT a pickup. A long low bonnet
// with a hood vent, a low raked cabin (the roof clearly NARROWER than the body,
// with a real shoulder line), then a SHORT CLOSED boot at belt height with a big
// raked backlight bridging the roof down to it (roof -> backlight -> boot read
// as one connected mass) and a short vertical tail. The defining cues: blistered
// muscular body-colour arches over all four wheels; THE ICONIC BIG REAR WING —
// a wide flat blade on two TALL uprights floating clearly above the boot with an
// obvious open gap under the blade; an aggressive squared face with a wide
// black-mesh grille in a chrome surround (a small round GT-R badge), angular
// swept headlamps flanking it, a deep front bumper with a big lower intake, side
// intakes, canards and round fog lamps; and the SKYLINE SIGNATURE at the rear:
// FOUR ROUND RED TAIL LAMPS (two per side) in a black tail panel over a diffuser
// with twin exhausts. Body panels carry the "paint" recolour slot (iconic
// Bayside Blue); `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#1f6fbf"; // iconic Skyline Bayside Blue — the "paint" slot default
const TRIM = "#141518"; // gloss black (wing, pillars, grille backing, window frames)
const CLAD = "#202225"; // matte black plastic (lower bumpers, splitter, diffuser)
const WELL = "#141518"; // dark wheel-well discs inside the arches
const GLASS = "#161c22"; // near-black side / rear glazing
const SCREEN = "#1f2933"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const CHROME = "#ccd1d6"; // grille surround, badges
const SPOKE = "#c4c9cf"; // bright five-spoke alloys
const HUB = "#6b7076"; // lighter grey alloy hub face
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const PROJ = "#8fa5b2"; // projector band inside the headlamp
const MESH = "#2a2d31"; // grille / intake black mesh
const AMBER = "#e8a13a";
const TAIL_RED = "#c31627"; // round tail lamps, deeper than the paint so they read
const TAIL_HOT = "#e0324a"; // bright inner glow of each round lamp
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). A LOW, WIDE, planted coupe: sill ~0.26, belt
// line ~0.84, roof peak a low ~1.18, a short CLOSED boot deck at belt height
// (~0.91). Track pushed right out so the tyres reach the body edges and the
// front reads markedly wider than tall; the rear is a touch wider under the
// muscular blistered haunches.
const WHEEL_R = 0.36; // big wheels that fill the arches
const AXLE_F = 1.15;
const AXLE_R = -1.52; // wheelbase 2.67
const WHEEL_X_F = 0.79; // half track, front
const WHEEL_X_R = 0.81; // half track, rear — wider under the haunches
const TYRE_W_F = 0.25;
const TYRE_W_R = 0.28;
const BODY_HW = 0.87; // body-tub half-width (1.74); arches blister to ~0.94

const SCREEN_RAKE = 0.68; // a low, well-raked windscreen (top toward the tail)
const HOOD_SLOPE = 0.05; // long low bonnet lid diving gently to the nose
// Backlight bridging the roof rear (z=-1.05, y=1.15) down to the boot lid
// (z=-1.30, y=0.90): a raked window so roof -> backlight -> boot read as one
// connected mass. The cabin is long and set back and the boot deck is a clean
// belt-height surface with the wing sitting on it, so the side reads as a coupe
// (long cabin, short tail with a wing), never a cab and an open bed.
const BACK_RAKE = 0.78;

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One big five-spoke alloy over a low-profile tyre: tyre, brake disc, dark
 *  dish, a grey hub face, five bright spokes proud of the tyre face, chrome
 *  centre cap. Every part shares `name` so the `drive` track spins them as one
 *  node about the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.15, 0.15, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.24, 0.24, 0.06, 18, DISH, [side * (outer - 0.055), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.21, 0.21, 0.03, 18, HUB, [side * (outer - 0.03), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.055, 0.4, 0.085, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.028, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** One blistered, squared, body-colour arch flare + its dark wheel well. The
 *  R34 arch is a muscular painted blister: a thin top brow plus short front and
 *  rear legs standing proud of the body side, tracing a squared wheel opening.
 *  The dark well disc hugs the tyre so there is no gap, and the tyre's outer
 *  face is proud of the disc so the alloy fills the arch. `lipX` is the outer
 *  face of the flare (the widest point of the car). */
function flare(side: 1 | -1, z: number, discX: number, discR: number, lipX: number): Part[] {
  const lx = side * lipX;
  return [
    cyl(discR, discR, 0.04, 20, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    paint(0.1, 0.1, 0.9, [lx, WHEEL_R + 0.36, z]), // squared top brow, just above the tyre
    paint(0.1, 0.42, 0.1, [lx, 0.46, z + 0.42]), // short front leg down to the sill
    paint(0.1, 0.42, 0.1, [lx, 0.46, z - 0.42]), // short rear leg down to the sill
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits in the lower bumper below the headlamps; rear pair is a small
// amber marker low in each corner of the black tail panel (kept clear of the
// four round red lamps that are the Skyline signature).
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.66, 0.46, 2.25], size: [0.16, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.66, 0.46, 2.25], size: [0.16, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.74, 0.5, -2.19], size: [0.14, 0.06, 0.04] },
  { name: "indicator-rr", pos: [0.74, 0.5, -2.19], size: [0.14, 0.06, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear -----------------------------------------------------------
  P.push(
    ...wheel(-1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
  }
  for (const s of S) {
    P.push(...flare(s, AXLE_F, 0.86, 0.46, 0.89)); // front blistered arch
    P.push(...flare(s, AXLE_R, 0.87, 0.47, 0.905)); // rear blistered arch, a touch wider
  }
  P.push(box(1.6, 0.14, 3.9, TRIM, [0, 0.16, -0.05])); // low underbody pan, closes the profile

  // -- Body masses (paint): a LOW, WIDE, muscular three-box, dropped on the
  //    wheels. Sill 0.26, belt/shoulder 0.84 — a low, planted tub. -----------
  P.push(paint(BODY_HW * 2, 0.58, 3.9, [0, 0.55, 0])); // main tub, sill (0.26) to shoulder (0.84)
  P.push(paint(BODY_HW * 2, 0.08, 3.4, [0, 0.84, -0.15])); // belt-line shoulder band
  // Muscular rear haunches: body-colour quarter blocks bulging past the tub
  // above each rear wheel (the R34's wide, squared rear shoulders).
  for (const s of S) P.push(paint(0.16, 0.34, 1.4, [s * 0.88, 0.55, -1.4])); // haunch quarter, outer ~0.96

  // Long low bonnet diving gently to the nose, with fender crowns beside it.
  P.push(paint(1.48, 0.09, 1.5, [0, 0.8, 1.28], [HOOD_SLOPE, 0, 0])); // bonnet lid
  for (const s of S) P.push(paint(0.2, 0.12, 1.5, [s * 0.79, 0.79, 1.28], [HOOD_SLOPE, 0, 0])); // fender crowns
  // Hood vent / power bulge (the R34's bonnet vent).
  P.push(paint(0.56, 0.05, 0.44, [0, 0.84, 1.42], [HOOD_SLOPE, 0, 0])); // raised bulge
  P.push(box(0.44, 0.03, 0.22, TRIM, [0, 0.86, 1.38], [HOOD_SLOPE, 0, 0])); // dark vent slot
  for (let i = -1; i <= 1; i++) P.push(box(0.4, 0.012, 0.02, "#33373d", [0, 0.875, 1.32 + i * 0.08], [HOOD_SLOPE, 0, 0])); // vent slats

  P.push(paint(BODY_HW * 2, 0.5, 0.18, [0, 0.58, 2.02])); // nose block behind the fascia
  P.push(paint(BODY_HW * 2, 0.58, 0.18, [0, 0.62, -2.02])); // tall tail block behind the fascia
  // The CLOSED boot: a solid block whose flat lid top sits just above the belt
  // line (~0.92), well below the roof, ending in a short vertical tail — a
  // sealed trunk continuing the body, NOT a low open bed.
  P.push(paint(BODY_HW * 2, 0.24, 0.72, [0, 0.8, -1.64])); // solid trunk block, flat lid top ~0.92
  P.push(paint(1.64, 0.05, 0.1, [0, 0.92, -1.96])); // ducktail lip along the trunk's trailing edge

  // -- Black plastic: sills, splitter, valance --------------------------------
  for (const s of S) P.push(box(0.06, 0.14, 2.6, CLAD, [s * 0.85, 0.3, -0.15])); // side sill, arch to arch
  P.push(box(1.5, 0.22, 0.3, TRIM, [0, 0.4, 1.86])); // under-nose fill (no see-through)
  P.push(box(1.5, 0.22, 0.3, TRIM, [0, 0.4, -1.86])); // under-tail fill

  // -- Greenhouse: a LOW, sealed, raked glasshouse, clearly NARROWER than the
  //    body (a real shoulder line). Roof half-width 0.75 vs body 0.87; the side
  //    glass tucks inboard of the roof edge so nothing wings past the roofline.
  P.push(box(1.44, 0.36, 1.86, CABIN, [0, 0.97, -0.4])); // dark interior mass behind the glass + backlight
  P.push(box(1.46, 0.04, 0.14, TRIM, [0, 0.85, 0.62])); // cowl at the screen base
  P.push(box(1.46, 0.42, 0.045, SCREEN, [0, 0.99, 0.36], [-SCREEN_RAKE, 0, 0])); // windscreen
  for (const s of S) P.push(paint(0.09, 0.44, 0.1, [s * 0.68, 0.99, 0.36], [-SCREEN_RAKE, 0, 0])); // raked A-pillars
  P.push(paint(1.5, 0.06, 1.5, [0, 1.15, -0.37])); // long roof, set back, overlapping screen + backlight
  for (const s of S) {
    P.push(box(0.04, 0.3, 1.26, GLASS, [s * 0.73, 0.98, -0.38])); // long side glass, sealed under the roof
    P.push(box(0.05, 0.3, 0.06, TRIM, [s * 0.737, 0.98, -0.34])); // B-pillar
    P.push(box(0.02, 0.03, 1.66, CHROME, [s * 0.875, 0.83, -0.38])); // belt-line chrome strip
  }
  // Solid body-colour C-pillar buttresses filling the rear quarters from the
  // roof down to the trunk lid, fairly upright, so from the SIDE the rear reads
  // as SOLID body (never a dark void). The backlight is a window recessed
  // BETWEEN them, inboard of the buttress inner edges.
  for (const s of S) P.push(paint(0.22, 0.4, 0.66, [s * 0.72, 1.0, -1.28], [0.42, 0, 0]));
  P.push(box(1.18, 0.36, 0.03, GLASS, [0, 1.02, -1.24], [BACK_RAKE, 0, 0])); // backlight recessed between the buttresses

  // -- THE BIG REAR WING: a wide flat blade on two slim uprights standing on the
  //    trunk lid, with an OBVIOUS open gap under the blade (blue trunk visible
  //    beneath). Kept wide/deep with thin end plates so it silhouettes as a
  //    horizontal wing, not a vertical tower. --------------------------------
  for (const s of S) P.push(box(0.06, 0.4, 0.09, TRIM, [s * 0.56, 1.12, -1.6])); // slim upright, 0.92 -> 1.32
  P.push(box(1.6, 0.05, 0.42, TRIM, [0, 1.31, -1.62])); // wide flat wing blade, floating above the trunk
  P.push(box(1.6, 0.03, 0.06, TRIM, [0, 1.32, -1.42])); // blade leading lip
  for (const s of S) P.push(box(0.04, 0.08, 0.46, TRIM, [s * 0.79, 1.31, -1.62])); // slim end plates (blade height)
  P.push(box(0.5, 0.02, 0.02, TAIL_RED, [0, 1.28, -1.74])); // high-level brake light on the wing

  // -- Front fascia: aggressive squared face -----------------------------------
  // Angular swept headlamps in the upper corners of the nose.
  for (const s of S) {
    P.push(box(0.52, 0.2, 0.06, TRIM, [s * 0.6, 0.74, 2.12])); // dark headlamp surround
    P.push(box(0.46, 0.16, 0.06, LAMP, [s * 0.6, 0.74, 2.16])); // headlamp lens
    P.push(box(0.34, 0.05, 0.03, PROJ, [s * 0.6, 0.78, 2.19])); // upper projector band
    P.push(cyl(0.055, 0.055, 0.03, 14, PROJ, [s * 0.7, 0.7, 2.19], [Math.PI / 2, 0, 0])); // outer projector (quad hint)
    P.push(cyl(0.055, 0.055, 0.03, 14, PROJ, [s * 0.5, 0.7, 2.19], [Math.PI / 2, 0, 0])); // inner projector (quad hint)
  }
  // Wide rectangular grille (black mesh in a chrome surround) with a round badge.
  P.push(box(0.9, 0.24, 0.05, CHROME, [0, 0.56, 2.13])); // grille surround
  P.push(box(0.82, 0.18, 0.04, MESH, [0, 0.56, 2.16])); // grille mesh
  P.push(cyl(0.055, 0.055, 0.02, 16, CHROME, [0, 0.56, 2.185], [Math.PI / 2, 0, 0])); // round GT-R badge
  P.push(cyl(0.03, 0.03, 0.022, 12, TAIL_RED, [0, 0.56, 2.19], [Math.PI / 2, 0, 0])); // badge red centre
  // Deep front bumper: big lower intake, side intakes, canards, fog lamps.
  P.push(box(1.66, 0.28, 0.14, CLAD, [0, 0.34, 2.16])); // black lower bumper
  P.push(box(0.94, 0.16, 0.04, MESH, [0, 0.36, 2.24])); // big lower intake
  for (const s of S) {
    P.push(box(0.24, 0.14, 0.04, MESH, [s * 0.62, 0.34, 2.23])); // side intake
    P.push(cyl(0.06, 0.06, 0.04, 14, LAMP, [s * 0.5, 0.34, 2.26], [Math.PI / 2, 0, 0])); // round fog lamp
    P.push(box(0.2, 0.02, 0.1, CLAD, [s * 0.82, 0.44, 2.13], [0, 0, s * 0.16])); // corner canard
  }
  P.push(box(1.62, 0.05, 0.24, CLAD, [0, 0.22, 2.18])); // front splitter
  P.push(box(0.42, 0.11, 0.02, PLATE, [0, 0.24, 2.21])); // front plate on the splitter

  // -- Rear fascia: the SKYLINE SIGNATURE four round tail lamps -----------------
  P.push(box(1.66, 0.3, 0.05, TRIM, [0, 0.64, -2.14])); // full-width black tail panel
  for (const s of S) {
    // Two round red lamps per side (four across the tail): outer + inner. Each
    // is a dark ring, a red lens proud of the panel, and a bright inner glow.
    for (const lx of [0.42, 0.68]) {
      const x = s * lx;
      P.push(cyl(0.14, 0.14, 0.03, 18, "#1a1b1d", [x, 0.64, -2.16], [Math.PI / 2, 0, 0])); // dark bezel ring
      P.push(cyl(0.115, 0.115, 0.04, 18, TAIL_RED, [x, 0.64, -2.185], [Math.PI / 2, 0, 0])); // red lens, proud
      P.push(cyl(0.07, 0.07, 0.02, 16, TAIL_HOT, [x, 0.64, -2.2], [Math.PI / 2, 0, 0])); // bright inner glow
    }
  }
  P.push(cyl(0.05, 0.05, 0.02, 16, CHROME, [0, 0.64, -2.17], [Math.PI / 2, 0, 0])); // centre badge
  // Rear diffuser + twin exhausts under the black panel.
  P.push(box(1.6, 0.24, 0.14, CLAD, [0, 0.32, -2.16])); // rear diffuser valance
  for (const s of S) for (let i = 0; i < 3; i++) {
    P.push(box(0.05, 0.14, 0.02, TRIM, [s * (0.2 + i * 0.16), 0.3, -2.23])); // diffuser fins
  }
  for (const dx of [-0.16, 0.16]) {
    P.push(cyl(0.07, 0.07, 0.1, 14, EXHAUST, [dx, 0.28, -2.24], [Math.PI / 2, 0, 0])); // twin round exhausts
    P.push(cyl(0.05, 0.05, 0.04, 14, "#0c0d0f", [dx, 0.28, -2.285], [Math.PI / 2, 0, 0])); // exhaust bore
  }
  P.push(box(0.42, 0.11, 0.02, PLATE, [0, 0.5, -2.2])); // rear plate on the panel

  // -- Furniture: mirrors, door handles, wipers --------------------------------
  for (const s of S) {
    P.push(box(0.08, 0.03, 0.05, TRIM, [s * 0.88, 0.86, 0.42])); // mirror stalk
    P.push(paint(0.09, 0.08, 0.13, [s * 0.94, 0.88, 0.4])); // mirror head
    P.push(box(0.012, 0.06, 0.1, GLASS, [s * 0.895, 0.88, 0.33])); // mirror glass, facing back
    P.push(box(0.02, 0.03, 0.14, CHROME, [s * 0.88, 0.68, -0.4])); // door handle
    P.push(box(0.32, 0.012, 0.03, TRIM, [s * 0.28, 0.77, 0.52], [-SCREEN_RAKE, 0, 0])); // wiper on the screen
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const nissanSkylineR34 = defineModel({
  id: "nissan-skyline-r34",
  name: "Nissan Skyline GT-R (R34)",
  batchId: "cars",
  tags: ["car", "nissan", "skyline", "gtr", "r34", "coupe", "sports", "jdm"],
  summary: "A Nissan Skyline GT-R (R34) sports coupe at true scale, in iconic Bayside Blue.",
  description:
    "A Nissan Skyline GT-R (R34, ~1999) at real 1 unit = 1 metre scale (about " +
    "4.60 long, 1.79 wide blistering wider over the arches, a low 1.36 tall, " +
    "2.67 wheelbase, on big pushed-out wheels). A low, wide, angular two-door " +
    "notchback coupe: a long low bonnet with a hood vent, a low raked cabin " +
    "whose roof is clearly narrower than the body, and a SHORT CLOSED boot at " +
    "belt height with a big raked backlight bridging the roof down to it and a " +
    "short vertical tail. The signature cues are all present: blistered muscular " +
    "body-colour arches over all four wheels; the big rear wing (a wide flat " +
    "blade on two tall uprights floating clearly above the boot); an aggressive " +
    "squared face with a wide black-mesh grille in a chrome surround and a round " +
    "GT-R badge, angular swept headlamps, a deep front bumper with a big lower " +
    "intake, side intakes, canards and round fog lamps; and at the rear the " +
    "Skyline signature four round red tail lamps (two per side) in a black panel " +
    "over a diffuser with twin exhausts, on bright five-spoke alloys. Body " +
    "panels carry the 'paint' recolour slot (iconic Bayside Blue); `drive` " +
    "spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.45,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
